import { createContext, useContext, useMemo, type ReactNode } from 'react';

/** Dotted permission keys from /me, e.g. "products.write", "staff.manage". */
export type Permission = string;

/** The owner role carries this instead of the full list. */
export const ALL_PERMISSIONS = '*';

const PermissionsContext = createContext<ReadonlySet<Permission>>(new Set());

export function PermissionsProvider({
  permissions,
  children,
}: {
  permissions: readonly Permission[];
  children: ReactNode;
}) {
  const set = useMemo(() => new Set(permissions), [permissions]);
  return <PermissionsContext.Provider value={set}>{children}</PermissionsContext.Provider>;
}

export function hasPermission(
  granted: ReadonlySet<Permission> | readonly Permission[],
  required: Permission | Permission[],
  mode: 'all' | 'any' = 'all',
): boolean {
  const set = granted instanceof Set ? granted : new Set(granted);
  if (set.has(ALL_PERMISSIONS)) return true;
  const list = Array.isArray(required) ? required : [required];
  if (list.length === 0) return true;
  return mode === 'all' ? list.every((p) => set.has(p)) : list.some((p) => set.has(p));
}

export function usePermissions() {
  const granted = useContext(PermissionsContext);
  return {
    granted,
    can: (required: Permission | Permission[], mode: 'all' | 'any' = 'all') =>
      hasPermission(granted, required, mode),
  };
}

export interface CanProps {
  permission: Permission | Permission[];
  mode?: 'all' | 'any';
  /** Rendered when the permission is missing. Defaults to nothing. */
  fallback?: ReactNode;
  children: ReactNode;
}

/** Gates UI on permissions. Route-level gating uses `requirePermission` in the route's beforeLoad. */
export function Can({ permission, mode = 'all', fallback = null, children }: CanProps) {
  const { can } = usePermissions();
  return can(permission, mode) ? children : fallback;
}
